/**
 * EntityCard — Unified card component for all entity types.
 *
 * ONE component that handles everything:
 *   - Header: Title, badges, context menu (•••)
 *   - Body: Children content (entity-specific details)
 *   - Footer: Divider, action buttons, Heart icon
 *
 * All styling uses ds tokens — no hardcoded values.
 *
 * Menu items (Edit, Details, Delete) are built internally from props.
 * No need to pass menuItems — just pass the callbacks.
 *
 * Usage:
 *   <EntityCard
 *     title="2023 Tesla Model Y"
 *     badge={{ label: "Car", color: "blue" }}
 *     canEdit={canEdit}
 *     canDelete={canDelete}
 *     onEdit={onEdit}
 *     onDelete={onDelete}
 *     onViewDetails={onViewDetails}
 *     footerButtons={[
 *       { icon: <FileText />, label: "Document", count: 3, onClick: openDocs },
 *     ]}
 *     isPrivate={vehicle.isPrivate}
 *     isFavorite={vehicle.isFavorite}
 *     onToggleFavorite={onToggleFavorite}
 *   >
 *     <div>VIN: •••••••4993</div>
 *     <div>License: ABC-1234</div>
 *   </EntityCard>
 */

import { useRef, useState } from 'react';
import { MoreHorizontal, Heart, Pencil, Trash2, Info, Check, Paperclip, Share2 } from 'lucide-react';
import { ds } from '../tokens';
import { Tooltip, ContextMenu, ContextMenuItem, ContextMenuDivider, Button, IconButton } from '../primitives';

// ── Badge colors ────────────────────────────────────────────

export type BadgeColor = 'blue' | 'green' | 'orange' | 'purple' | 'gray' | 'red' | 'teal';

const badgeColors: Record<BadgeColor, { bg: string; text: string }> = {
  blue: { bg: ds.color.chip.blue.bg, text: ds.color.chip.blue.text },
  green: { bg: ds.color.chip.green.bg, text: ds.color.chip.green.text },
  orange: { bg: ds.color.chip.orange.bg, text: ds.color.chip.orange.text },
  purple: { bg: ds.color.chip.purple.bg, text: ds.color.chip.purple.text },
  gray: { bg: ds.color.chip.gray.bg, text: ds.color.chip.gray.text },
  red: { bg: ds.color.chip.red.bg, text: ds.color.chip.red.text },
  teal: { bg: ds.color.chip.teal.bg, text: ds.color.chip.teal.text },
};

// ── Menu item types ─────────────────────────────────────────

export interface CardMenuAction {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
}

export interface CardMenuDivider {
  divider: true;
}

export type CardMenuItem = CardMenuAction | CardMenuDivider;

// ── Badge type ──────────────────────────────────────────────

export interface CardBadge {
  label: string;
  color: BadgeColor;
}

// ── Footer button type ──────────────────────────────────────

export interface FooterButton {
  icon: React.ReactNode;
  /** Singular label (e.g. "Document", "Contact", "ID Card") — used for tooltip and aria */
  label: string;
  /** Item count — shown as badge, also drives tooltip text */
  count?: number;
  onClick: () => void;
}

// ── Component ───────────────────────────────────────────────

export interface EntityCardProps {
  /** Card title */
  title: string;
  /** Optional badge/chip next to title */
  badge?: CardBadge;
  /** Additional badges */
  badges?: CardBadge[];
  /** Card body content */
  children: React.ReactNode;
  /** Data-driven footer action buttons (rendered as Tooltip + IconButton) */
  footerButtons?: FooterButton[];
  /** Whether item is the primary/default entity (shows green tick) */
  isDefault?: boolean;
  /** Whether item is favorited (shows filled heart) */
  isFavorite?: boolean;
  /** Can user edit (enables interactive icons) */
  canEdit: boolean;
  /** Can user delete */
  canDelete?: boolean;
  /** Called when edit is needed */
  onEdit?: () => void;
  /** Called when delete is needed */
  onDelete?: () => void;
  /** Called when details/view is needed (also used for card click) */
  onViewDetails?: () => void;
  /** Called when heart is clicked */
  onToggleFavorite?: () => void;
  /** Called when card is clicked */
  onClick?: () => void;
  /** Called when Details chip is clicked (renders Details pill in footer) */
  onOpenDetails?: () => void;
  /** Called when Links chip is clicked (renders Links pill in footer) */
  onOpenLinks?: () => void;
  /** Called when Share button is clicked (shown in footer right) */
  onShare?: () => void;
  /** Number of times this entity has been shared */
  shareCount?: number;
  /** Number of linked portals (shown as badge when > 0) */
  linksCount?: number;
  /** Show loading spinner on Links chip (replaces count badge) */
  linksLoading?: boolean;
  /** Total attachment count (photos + documents) — shown as paperclip icon with count */
  attachmentCount?: number;
  /** Called when attachment icon is clicked */
  onOpenAttachments?: () => void;
  /** Extra content rendered after badges in the header (e.g. status dot) */
  headerExtra?: React.ReactNode;
  /** Extra content rendered before the heart icon in the footer right section */
  footerExtra?: React.ReactNode;
}

export function EntityCard({
  title,
  badge,
  badges = [],
  children,
  footerButtons = [],
  isDefault = false,
  isFavorite = false,
  canEdit,
  canDelete = false,
  onEdit,
  onDelete,
  onViewDetails,
  onToggleFavorite,
  onClick,
  onOpenDetails,
  onOpenLinks,
  onShare,
  shareCount = 0,
  linksCount = 0,
  linksLoading = false,
  attachmentCount = 0,
  onOpenAttachments,
  headerExtra,
  footerExtra,
}: EntityCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef<HTMLDivElement>(null);

  const allBadges = badge ? [badge, ...badges] : badges;

  // Build standard menu items internally
  const menuItems: CardMenuItem[] = [];
  if (canEdit && onEdit) {
    menuItems.push({ icon: <Pencil className={ds.icon.sm} />, label: 'Edit', onClick: onEdit });
  }
  if (onViewDetails) {
    menuItems.push({ icon: <Info className={ds.icon.sm} />, label: 'Details', onClick: onViewDetails });
  }
  if (canDelete && onDelete) {
    menuItems.push({ divider: true });
    menuItems.push({ icon: <Trash2 className={ds.icon.sm} />, label: 'Delete', onClick: onDelete, variant: 'danger' });
  }

  const hasMenu = menuItems.length > 0;
  const cardClick = onClick || onViewDetails;
  const hasFooter = footerButtons.length > 0 || onOpenDetails || onOpenLinks || (canEdit && onToggleFavorite);

  return (
    <div
      className={[
        'relative',
        ds.color.fill.grouped,
        ds.radius.lg,
        ds.shadow.card,
        'border',
        ds.color.separator.light,
        'h-full flex flex-col',
        'p-5 sm:p-6',
        cardClick ? `cursor-pointer ${ds.transition.fast} active:scale-[0.98]` : '',
      ].filter(Boolean).join(' ')}

      onClick={cardClick || undefined}
    >
      {/* Stretched click target — keyboard-only; mouse clicks handled by container onClick */}
      {cardClick && (
        <button
          type="button"
          className="absolute inset-0 z-[1] pointer-events-none rounded-[inherit] focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2"
          onClick={cardClick}
          aria-label={`View ${title}`}
          tabIndex={0}
        />
      )}
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="relative z-[2] flex items-start justify-between mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-2">
            <h3 className={`${ds.type.cardTitle} ${ds.type.weight.semibold} ${ds.color.label.primary} truncate min-w-0`}>
              {title}
            </h3>

            {isDefault && (
              <Tooltip text="Primary">
                <div
                  className={`${ds.icon.sm} ${ds.radius.full} flex items-center justify-center flex-shrink-0`}
                  style={{ backgroundColor: ds.color.raw.systemGreen }}
                >
                  <Check className="w-2.5 h-2.5 text-white stroke-[3]" />
                </div>
              </Tooltip>
            )}

            {allBadges.map((b, i) => (
              <span
                key={i}
                className={`${ds.color.chip.base} flex-shrink-0`}
                style={{ backgroundColor: badgeColors[b.color || 'blue'].bg, color: badgeColors[b.color || 'blue'].text }}
              >
                {b.label}
              </span>
            ))}

            {headerExtra}
          </div>
        </div>

        {/* Action menu */}
        {hasMenu && (
          <div className="flex items-center flex-shrink-0" onClick={e => e.stopPropagation()}>
            <div ref={menuTriggerRef}>
              <Tooltip text={ds.tooltip.more} disabled={menuOpen}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className={`inline-flex items-center justify-center ${ds.transition.fast}`}
                  aria-label="More options"
                >
                  <MoreHorizontal className={`${ds.icon.sm} ${ds.interaction.actionIcon}`} />
                </button>
              </Tooltip>
            </div>
            <ContextMenu isOpen={menuOpen} onClose={() => setMenuOpen(false)} anchorEl={menuTriggerRef.current}>
              {menuItems.map((item, i) => {
                if ('divider' in item) {
                  return <ContextMenuDivider key={i} />;
                }
                return (
                  <ContextMenuItem
                    key={i}
                    icon={item.icon}
                    label={item.label}
                    onClick={() => { setMenuOpen(false); item.onClick(); }}
                    variant={item.variant}
                  />
                );
              })}
            </ContextMenu>
          </div>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────── */}
      <div className="space-y-2 flex-1 pb-2">
        {children}
      </div>

      {/* ── Footer ─────────────────────────────────────────── */}
      {hasFooter && (
        <div className="relative z-[2] mt-auto pt-4" onClick={e => e.stopPropagation()}>
          <div className={`absolute top-0 left-0 right-0 h-px ${ds.color.separator.divider}`} />
          <div className="flex items-center justify-between">
            {/* Left: Action buttons */}
            <div className={`flex items-center ${ds.interaction.actionIconGap}`}>
              {onOpenDetails && (
                <Button
                  variant="tonal"
                  size="pill"
                  onClick={onOpenDetails}
                >
                  Details
                </Button>
              )}
              {onOpenLinks && (
                <Button
                  variant="tonal"
                  size="pill"
                  onClick={onOpenLinks}
                  badge={!linksLoading ? linksCount : undefined}
                  badgeLoading={linksLoading}
                >
                  Links
                </Button>
              )}
              {footerButtons.map((btn, i) => {
                const tooltipText = btn.count && btn.count > 0
                  ? `${btn.count} ${btn.label}${btn.count > 1 ? 's' : ''}`
                  : `${btn.label}s`;
                return (
                  <Tooltip key={i} text={tooltipText}>
                    <IconButton
                      icon={btn.icon}
                      label={btn.label}
                      badge={btn.count}
                      size="sm"
                      color={ds.color.raw.systemBlue}
                      onClick={btn.onClick}
                    />
                  </Tooltip>
                );
              })}
              {attachmentCount > 0 && (
                <Tooltip text={`${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`}>
                  <IconButton
                    icon={<Paperclip className={ds.icon.sm} />}
                    label="Attachment"
                    badge={attachmentCount}
                    size="sm"
                    color={ds.color.raw.systemBlue}
                    onClick={onOpenAttachments}
                  />
                </Tooltip>
              )}
            </div>

            {/* Right: Extra + Heart + Share */}
            <div className="flex items-center gap-1">
              {footerExtra}
              {/* Heart icon — favorite toggle */}
              {canEdit && onToggleFavorite && (
                <Tooltip text={isFavorite ? ds.tooltip.unfavorite : ds.tooltip.favorite}>
                  <button
                    onClick={() => onToggleFavorite()}
                    className={`flex items-center justify-center ${ds.icon.buttonSm} ${ds.transition.fast}`}
                    aria-label={isFavorite ? 'Unfavorite' : 'Favorite'}
                  >
                    <Heart
                      className={`${ds.icon.footer} ${isFavorite ? '' : ds.interaction.actionIcon}`}
                      style={isFavorite ? { fill: ds.color.raw.systemRed, color: ds.color.raw.systemRed } : undefined}
                    />
                  </button>
                </Tooltip>
              )}

              {/* Share button — same style as contacts/attachments */}
              {onShare && (
                <Tooltip text={shareCount ? `Shared ${shareCount} time${shareCount !== 1 ? 's' : ''}` : 'Share'}>
                  <IconButton
                    icon={<Share2 className={ds.icon.sm} />}
                    label="Share"
                    badge={shareCount || undefined}
                    size="sm"
                    color={ds.color.raw.systemBlue}
                    onClick={onShare}
                  />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
